import { Chart, registerables } from 'chart.js'
import { useEffect, useRef } from 'react'
import { DimensionMetrics } from '../../../types/metrics'
import InfoTooltip from '../../atoms/InfoTooltip'

Chart.register(...registerables)

type Props = {
  metrics: DimensionMetrics
}

const DIMENSION_LABELS = ['Growth Mindset', 'Communication', 'Commitment', 'Prof. Skills', 'Productivity']
const DIMENSION_KEYS: (keyof Pick<
  DimensionMetrics['overall'],
  'growthMindset' | 'communication' | 'commitment' | 'professionalSkills' | 'productivity'
>)[] = ['growthMindset', 'communication', 'commitment', 'professionalSkills', 'productivity']

const SKELETON_BARS: { key: (typeof DIMENSION_KEYS)[number]; width: number }[] = [
  { key: 'growthMindset', width: 60 },
  { key: 'communication', width: 75 },
  { key: 'commitment', width: 50 },
  { key: 'professionalSkills', width: 85 },
  { key: 'productivity', width: 70 },
]

const DimensionAveragesChart = ({ metrics }: Props): JSX.Element => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart<'bar'> | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    chartRef.current?.destroy()

    const values = DIMENSION_KEYS.map(k => metrics.overall[k] ?? 0)

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels: DIMENSION_LABELS,
        datasets: [
          {
            label: 'Average',
            data: values,
            backgroundColor: values.map(v => (v >= 80 ? '#2D6A4F' : v >= 65 ? '#D4A017' : '#C0392B')),
            borderRadius: 6,
            barThickness: 28,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => {
                const idx = ctx.dataIndex
                const key = DIMENSION_KEYS[idx]
                const avg = metrics.overall[key] ?? 0
                const p25 = metrics.percentiles.p25[key] ?? 0
                const p50 = metrics.percentiles.p50[key] ?? 0
                const p75 = metrics.percentiles.p75[key] ?? 0
                return [`Average: ${avg}/100`, `P25: ${p25}  ·  Median: ${p50}  ·  P75: ${p75}`]
              },
            },
          },
        },
        scales: {
          x: {
            min: 0,
            max: 100,
            ticks: { font: { size: 13 } },
            grid: { color: '#F3F4F6' },
          },
          y: {
            ticks: { font: { size: 13, weight: 'bold' } },
            grid: { display: false },
          },
        },
      },
    })

    return () => {
      chartRef.current?.destroy()
    }
  }, [metrics])

  const { overall } = metrics
  const summary = DIMENSION_KEYS.map((k, i) => `${DIMENSION_LABELS[i]}: ${overall[k] ?? '–'}`).join(', ')

  return (
    <div className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
      <div className="mb-4 flex items-center gap-2">
        <h3 className="text-2xl font-bold text-dark-blue">Dimension averages (0–100)</h3>
        <InfoTooltip text="Mean score across all completed reviews for each of the six assessment dimensions." />
      </div>
      <div role="img" aria-label={`Dimension averages: ${summary}`} style={{ height: 280 }}>
        <canvas ref={canvasRef} />
      </div>
      <p className="mt-3 text-xl text-stone-gray">Hover a bar to see P25 / median / P75 percentiles.</p>
    </div>
  )
}

export const DimensionAveragesChartSkeleton = (): JSX.Element => (
  <div aria-hidden="true" className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
    <div className="mb-4 h-7 w-3/4 rounded bg-soft-gray animate-pulse" />
    <div className="flex flex-col gap-3" style={{ height: 280 }}>
      {SKELETON_BARS.map(({ key, width }) => (
        <div key={key} className="flex items-center gap-3">
          <div className="h-4 w-24 rounded bg-soft-gray animate-pulse" />
          <div className="h-7 rounded bg-soft-gray animate-pulse" style={{ width: `${width}%` }} />
        </div>
      ))}
    </div>
  </div>
)

export default DimensionAveragesChart
